import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  IconButton,
  Box,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import {
  Mic,
  MicOff,
  Stop,
  VolumeUp
} from '@mui/icons-material';

const SpeechToTextInput = ({
  value,
  onChange,
  onKeyPress,
  placeholder = "Type your message or click the microphone to speak...",
  disabled = false,
  multiline = true,
  maxRows = 10, // Increased default max rows for auto-expansion
  variant = "standard",
  size = "small",
  sx = {},
  fullWidth = true,
  ...otherProps
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);

  // Check if speech recognition is supported
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setIsSupported(true);
      recognitionRef.current = new SpeechRecognition();
      
      // Configure speech recognition
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      // Handle results
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          setTranscript(prev => prev + finalTranscript);
          // Update the input field with the final transcript
          const newValue = value + finalTranscript;
          onChange({ target: { value: newValue } });
        }
        
        setInterimTranscript(interimTranscript);
      };
      
      // Handle errors
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setError(`Speech recognition error: ${event.error}`);
        setIsListening(false);
        
        // Clear error after 3 seconds
        setTimeout(() => setError(''), 3000);
      };
      
      // Handle end of recognition
      recognitionRef.current.onend = () => {
        setIsListening(false);
        setInterimTranscript('');
      };
      
      // Handle start of recognition
      recognitionRef.current.onstart = () => {
        setError('');
        setTranscript('');
        setInterimTranscript('');
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startListening = () => {
    if (!isSupported || !recognitionRef.current) {
      setError('Speech recognition is not supported in your browser');
      return;
    }
    
    try {
      setIsListening(true);
      setError('');
      recognitionRef.current.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setError('Failed to start speech recognition');
      setIsListening(false);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Get microphone button color and icon based on state
  const getMicrophoneButton = () => {
    if (!isSupported) {
      return (
        <Tooltip title="Speech recognition not supported in your browser">
          <span>
            <IconButton disabled size="small">
              <MicOff sx={{ fontSize: 18, color: '#ccc' }} />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    if (isListening) {
      return (
        <Tooltip title="Click to stop recording">
          <IconButton 
            onClick={stopListening}
            size="small"
            sx={{ 
              color: '#f44336',
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.2)',
              }
            }}
          >
            <Stop sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Click to start voice input">
        <IconButton 
          onClick={startListening}
          disabled={disabled}
          size="small"
          sx={{ 
            color: '#008080',
            '&:hover': {
              backgroundColor: 'rgba(0, 128, 128, 0.1)',
            }
          }}
        >
          <Mic sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* Main input field */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
        <TextField
          fullWidth={fullWidth}
          multiline={multiline}
          minRows={1} // Start with 1 row
          maxRows={maxRows}
          value={value + (interimTranscript ? ` ${interimTranscript}` : '')}
          onChange={onChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          variant={variant}
          size={size}
          disabled={disabled}
          sx={{
            ...sx,
            '& .MuiInput-root': {
              fontSize: '14px',
              minHeight: 'auto', // Allow auto height
              '&:before': { border: 'none' },
              '&:after': { border: 'none' },
              '&:hover:before': { border: 'none' },
              '&:focus-within:before': { border: 'none' }
            },
            '& .MuiInput-input': {
              padding: '8px 12px',
              resize: 'none', // Disable manual resize
              overflow: 'hidden !important', // Hide scrollbars
              '&::placeholder': {
                color: '#9ca3af',
                opacity: 1
              }
            },
            // Hide scrollbars completely
            '& textarea': {
              overflow: 'hidden !important',
              resize: 'none !important',
              scrollbarWidth: 'none !important', // Firefox
              '-ms-overflow-style': 'none !important', // IE/Edge
              '&::-webkit-scrollbar': {
                display: 'none !important' // Chrome/Safari
              }
            }
          }}
          {...otherProps}
        />
        
        {/* Microphone button */}
        {getMicrophoneButton()}
      </Box>
      
      {/* Status indicators */}
      {isListening && (
        <Box sx={{ 
          position: 'absolute', 
          top: -30, 
          left: 0, 
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Chip
            icon={<VolumeUp sx={{ fontSize: 14 }} />}
            label="Listening..."
            size="small"
            color="primary"
            variant="outlined"
            sx={{ 
              fontSize: '11px',
              height: 24,
              backgroundColor: 'rgba(0, 128, 128, 0.1)',
              borderColor: '#008080',
              color: '#008080'
            }}
          />
        </Box>
      )}
      
      
      {/* Error display */}
      {error && (
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute',
            bottom: -20,
            left: 0,
            color: '#f44336',
            fontSize: '11px'
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default SpeechToTextInput;
